import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import * as Styles from './ChangeDomainHandler.styles';
import HgLogoV2 from '@/media/logo/HgLogoV2';
import { Redirect, useHistory, useParams } from 'react-router';
import { STEPS } from './ChangeDomainHandler.enum';
import useLocale from '@/hooks/useLocale';
import { DomainNotRegistered } from './components/DomainNotRegistered';
import { useDispatch, useSelector } from 'react-redux';
import InputDomain from './components/InputDomain/InputDomain';
import { BreadCrumbs } from './components/BreadCrumbs';
import { routesV2 } from '@/routes/routes';
import { sitesActions, domainsActions } from '@/redux/modules';
import { domain as isDomain } from '@/utils/Validators/validation';
import { ExternalDomain } from './components/ExternalDomain';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import {
  ATIVACAO_CHANGE_DOMAIN_FLOW, ATIVACAO_CHANGEDOMAIN_FAILED_WHOIS, ATIVACAO_MODAL_REINFORCMENT_CHANGE_DOMAIN_FLOW,
} from '@/config/GrowthBook/constants';
import { ChangeDomainSuccess } from './components/ChangeDomainSuccess';
import SnappyIdea from '@/media/domains/snappy-idea.svg';
import { Skeleton } from 'gatorcomponents';
import { CART_URL } from '@/config';
import { locale } from '@/utils/locale';
import { NavigatorDetail } from '@/media/changeDomain';
import { useResize } from '@/hooks/useResize';
import { HgProducts } from '@/components/SupportForms/SupportForms.types';
import { ReinforcementModal } from './components/ReinforcementModal';

const ChangeDomainHandler = () => {
  const params = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const windowRect = useResize();
  const { width } = windowRect;
  const mobile = width < 960;
  const { changeDomain: changeDomainLocale, routes: routesLocale } = useLocale();
  const enabledChangeDomainFlow = useFeatureIsOn(ATIVACAO_CHANGE_DOMAIN_FLOW);
  const withReinforcementModal = useFeatureIsOn(ATIVACAO_MODAL_REINFORCMENT_CHANGE_DOMAIN_FLOW);

  const initialStep = STEPS.INPUT_DOMAIN;

  const [currentStep, setCurrentStep] = useState(initialStep);
  const [page, setPage] = useState(1);
  const [showAutoCompleteDomain, setShowAutoCompleteDomain] = useState(false);
  const [autoCompleteOptions, setAutoCompleteOptions] = useState([]);
  const [domainIsOk, setDomainIsOk] = useState(false);
  const [newDomain, setNewDomain] = useState({
    value: '',
    error: '',
  });
  const [showReinforcementModal, setShowReinforcementModal] = useState(false);
  const ignoreFailedWhois = useFeatureIsOn(ATIVACAO_CHANGEDOMAIN_FAILED_WHOIS);

  const domainsReducer = useSelector(state => state.newDomains);
  const sitesReducer = useSelector(state => state.sites);

  const cleanUpFunction = useCallback(() => {
    dispatch(domainsActions.changeDomain.resetState());
    dispatch(sitesActions.domainDns.resetStatus());
    dispatch(sitesActions.planDns.reset());
  }, [dispatch]);

  useEffect(() => () => {
    cleanUpFunction();
  }, [cleanUpFunction]);

  const isInternalDomain = domainsReducer.list && domainsReducer.list.find(item => item.domain === newDomain.value);
  const currentPlan = sitesReducer.list && sitesReducer.list.find(item => item.hosting_id === Number(params.originalHostingId));
  const currentDomain = currentPlan && currentPlan.cpanel_domains.find(cpanelItem => cpanelItem.domain === params.originalDomainName);
  const isAddonDomain = currentDomain && currentDomain.type !== 'main';

  const hasSites = !!sitesReducer.list.length;
  const hostingData = hasSites && sitesReducer.list.find(hosting => hosting.hosting_id === Number(params.originalHostingId));
  const isSharedHosting = hostingData && HgProducts.SHARED_ALL.includes(hostingData.package_id);
  const isResellerHosting = hostingData && HgProducts.RESELLER_ALL.includes(hostingData.package_id);
  const canChangeDomain = isSharedHosting || isResellerHosting;

  const updateOriginalDomainName = useCallback((newDomainName) => {
    const updatedDomainName = newDomainName;

    history.replace({
      pathname: `${routesLocale.sitesPage}${routesLocale.changeDomain}/${params.originalHostingId}/${updatedDomainName}`,
      search: history.location.search,
    });
  }, [
    history,
    params.originalHostingId,
    routesLocale.changeDomain,
    routesLocale.sitesPage,
  ]);

  useEffect(() => {
    if (!params.originalDomainName) {
      history.push(routesLocale.sitesPage);
    }
  }, [
    params.originalDomainName,
    history,
    routesLocale.sitesPage,
  ]);

  const changeStepAfterAttempt = useCallback(() => {
    if (domainsReducer.changeDomain.lastAttempt) {
      if (
        domainsReducer.changeDomain.lastAttempt === 'success'
        && domainsReducer.changeDomain.newDomain
        && domainsReducer.changeDomain.newDomain !== ''
        && currentStep === STEPS.INPUT_DOMAIN
      ) {
        updateOriginalDomainName(newDomain.value);
        setCurrentStep(STEPS.SUCCESS);
      }
    }
  }, [
    currentStep,
    domainsReducer.changeDomain.lastAttempt,
    domainsReducer.changeDomain.newDomain,
    newDomain.value,
    updateOriginalDomainName,
  ]);

  useEffect(() => {
    changeStepAfterAttempt();
  }, [changeStepAfterAttempt]);

  const url = new URL(window.location.href);
  const paths = url.pathname.split('/').filter(path => path !== '');

  const userLastPath = `/${paths[0]}`;
  const userLastPathData = routesV2(locale).find(route => route.path === userLastPath);

  const initialBreadCrumbs = useMemo(() => [
    {
      label: userLastPathData.name || 'Sites',
      onClick: () => history.push(userLastPath || '/sites'),
    },
  ], [userLastPath, userLastPathData, history]);

  const [breadCrumbs, setBreadCrumbs] = useState(initialBreadCrumbs);

  const changeDomainSuccessProps = useCallback(() => {
    const goToLastPage = () => {
      history.push(`/${params.lastPage}`);
    };

    return {
      titleLabel: changeDomainLocale.steps.success.title,
      descriptionLabel: changeDomainLocale.steps.success.description,
      helpLabel: changeDomainLocale.steps.success.helpLabel,
      goToLastPageButtonLabel: changeDomainLocale.steps.success.backToSitesButtonLabel,
      goToLastPage,
    };
  }, [changeDomainLocale, history, params.lastPage]);

  const domainNotRegisteredSuccessProps = useCallback(() => {
    const goToLastPage = () => {
      history.push(`/${params.lastPage}`);
    };

    const {
      title,
      description,
      helpLabel,
      backToSitesButtonLabel,
    } = changeDomainLocale.steps.domainNotRegisteredSuccess;

    return {
      titleLabel: title,
      descriptionLabel: description,
      helpLabel,
      goToLastPageButtonLabel: backToSitesButtonLabel,
      goToLastPage,
      SnappyImage: SnappyIdea,
    };
  }, [changeDomainLocale, history, params.lastPage]);

  const getDomainsNextPage = useCallback(() => {
    if (page > 1) {
      dispatch(domainsActions.domains.request({ page }));
    }
  }, [
    dispatch,
    page,
  ]);

  useEffect(() => {
    getDomainsNextPage();
  }, [getDomainsNextPage]);

  const requestData = useCallback(() => {
    if (!sitesReducer.sitesLoaded) {
      dispatch(sitesActions.sites.request());
    }

    if (!domainsReducer.loaded) {
      dispatch(domainsActions.domains.request({ page }));
    }

    if (!domainsReducer.temporaryDomainsLoaded) {
      dispatch(domainsActions.temporaryDomains.request());
    }
  }, [
    dispatch,
    domainsReducer.loaded,
    page,
    domainsReducer.temporaryDomainsLoaded,
    sitesReducer.sitesLoaded,
  ]);

  useEffect(() => {
    requestData();
  }, [requestData]);


  const redirectToExternalDomainStep = useCallback(() => {
    const domainWhoisHasLoaded = sitesReducer.domainDns && !!sitesReducer.domainDns.length;
    const planDnsHasLoaded = sitesReducer.planDns && !!sitesReducer.planDns.length;
    const lastAttemptSuccess = domainsReducer.changeDomain.lastAttempt === 'success';

    if ((!isInternalDomain && domainWhoisHasLoaded && planDnsHasLoaded && lastAttemptSuccess)
    || (!isInternalDomain && domainWhoisHasLoaded && ignoreFailedWhois && lastAttemptSuccess)
    ) {
      updateOriginalDomainName(newDomain.value);
      setCurrentStep(STEPS.EXTERNAL_DOMAIN);
    }
  }, [
    isInternalDomain,
    sitesReducer.domainDns,
    sitesReducer.planDns,
    domainsReducer.changeDomain.lastAttempt,
    newDomain,
    updateOriginalDomainName,
    ignoreFailedWhois,
  ]);

  const crumbByStep = useMemo(() => ({
    [STEPS.INPUT_DOMAIN]: {
      label: changeDomainLocale.breadCrumbs.inputDomain,
      onClick: () => setCurrentStep(STEPS.INPUT_DOMAIN),
    },
    [STEPS.EXTERNAL_DOMAIN]: {
      label: changeDomainLocale.breadCrumbs.externalDomain,
      onClick: () => setCurrentStep(STEPS.EXTERNAL_DOMAIN),
    },
    [STEPS.DOMAIN_NOT_REGISTERED]: {
      label: changeDomainLocale.breadCrumbs.domainNotRegistered,
      onClick: () => setCurrentStep(STEPS.DOMAIN_NOT_REGISTERED),
    },
    [STEPS.SUCCESS]: {
      label: changeDomainLocale.breadCrumbs.success,
      onClick: () => setCurrentStep(STEPS.SUCCESS),
    },
    [STEPS.EXTERNAL_SUCCESS]: {
      label: changeDomainLocale.breadCrumbs.success,
      onClick: () => setCurrentStep(STEPS.EXTERNAL_SUCCESS),
    },
    [STEPS.NOT_REGISTERED_SUCCESS]: {
      label: changeDomainLocale.breadCrumbs.success,
      onClick: () => setCurrentStep(STEPS.NOT_REGISTERED_SUCCESS),
    },
  }), [changeDomainLocale]);

  const setBreadCrumbsByStep = useCallback(() => {
    const newBreadCrumb = {
      label: crumbByStep[currentStep].label,
      onClick: crumbByStep[currentStep].onClick,
    };

    const stepIsOnBreadCrumbs = breadCrumbs.some(breadCrumb => breadCrumb.label === newBreadCrumb.label);

    if (stepIsOnBreadCrumbs) {
      const SelectedItemIndex = breadCrumbs.findIndex(breadCrumb => breadCrumb.label === newBreadCrumb.label);
      const newList = breadCrumbs.slice(0, SelectedItemIndex + 1);
      return setBreadCrumbs(newList);
    }

    setBreadCrumbs(prev => ([...prev, newBreadCrumb]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, crumbByStep]);

  useEffect(() => {
    redirectToExternalDomainStep();
  }, [redirectToExternalDomainStep]);

  useEffect(() => {
    setBreadCrumbsByStep();
  }, [setBreadCrumbsByStep]);

  const autoCompleteDomain = useCallback(({ domain }) => {
    if (!domainsReducer.loading) {
      let foundDomain = false;
      const domainsAlike = domainsReducer.list.filter((item) => {
        if (!foundDomain) {
          if (item.domain === domain) {
            foundDomain = true;
            setShowAutoCompleteDomain(false);
            return item.domain;
          }
          return item.domain.includes(domain);
        }
        return false;
      });

      if (domainsReducer.list.length < domainsReducer.listTotal && !domainsReducer.loading) {
        setPage(page + 1);
      }

      setShowAutoCompleteDomain(!!domainsAlike.length);
      setAutoCompleteOptions(domainsAlike);
    }
  }, [
    domainsReducer.list,
    domainsReducer.listTotal,
    domainsReducer.loading,
    page,
  ]);

  const requestChangeDomain = useCallback(({ awaitForAppointment = false, domainId = null }) => {
    dispatch(domainsActions.changeDomain.request({
      domain: newDomain.value,
      hostingId: params.originalHostingId,
      isAddonDomain: !!isAddonDomain,
      awaitForAppointment,
      domainId,
      oldDomain: params.originalDomainName,
    }));
  }, [dispatch, newDomain.value, isAddonDomain, params]);

  const loadingChange = domainsReducer.changeDomain.loading || sitesReducer.loading || sitesReducer.planDnsLoading || sitesReducer.domainDnsLoading;

  const handleConfirmChangeDomain = useCallback(() => {
    const domain = newDomain.value.replace(/^www\./, '');

    const setDomainRegistered = () => {
      requestChangeDomain({});
    };

    const setDomainNotRegistered = () => {
      setCurrentStep(STEPS.DOMAIN_NOT_REGISTERED);
    };

    if (isInternalDomain) {
      const domainData = domainsReducer.list.find(item => item.domain === domain);
      requestChangeDomain({ awaitForAppointment: true, domainId: domainData && domainData.id });
    }

    if (!isInternalDomain) {
      dispatch(sitesActions.domainDns.request({
        domain,
        onDomainRegitered: setDomainRegistered,
        onDomainNotRegistered: setDomainNotRegistered,
      }));
      dispatch(sitesActions.planDns.request(params.originalHostingId));
    }
  }, [
    dispatch,
    domainsReducer.list,
    isInternalDomain,
    newDomain.value,
    params.originalHostingId,
    requestChangeDomain,
  ]);

  const getInputDomainProps = useCallback(() => {
    const defaultProps = {
      title: changeDomainLocale.steps.inputDomain.title,
      description: changeDomainLocale.steps.inputDomain.description,
      continueCta: changeDomainLocale.steps.inputDomain.continueCta,
      backCta: changeDomainLocale.steps.inputDomain.backCta,
      alertDescription: changeDomainLocale.steps.inputDomain.alertDescription,
      toggleCta: changeDomainLocale.steps.inputDomain.toggleCta,
      toggledContent: changeDomainLocale.steps.inputDomain.toggledContent,
      temporaryToggledContent: changeDomainLocale.steps.inputDomain.temporaryToggledContent,
      newDomain,
      showAutoCompleteDomain,
      autoCompleteOptions,
      domainIsOk,
      loadingChange,
      originalDomain: params.originalDomainName,
      mySiteLabel: changeDomainLocale.steps.inputDomain.mySiteLabel,
    };

    const isTemp = !!(enabledChangeDomainFlow && domainsReducer.temporaryDomains.find(item => item.domain === params.originalDomainName));

    const validateDomain = (domain) => {
      const domainIsValid = isDomain(domain);

      if (domainIsValid) {
        setDomainIsOk(true);
        setNewDomain({
          value: domain,
          error: domain === params.originalDomainName ? changeDomainLocale.steps.inputDomain.domainEqualOriginalDomain : '',
        });
      }

      if (!domainIsValid) {
        setDomainIsOk(false);
        if (newDomain.value.includes('.br')) {
          setNewDomain({
            value: domain,
            error: changeDomainLocale.steps.inputDomain.domainMessageErrorBr,
          });
          return;
        }

        if (!newDomain.value.includes('.br')) {
          setNewDomain({
            value: domain,
            error: changeDomainLocale.steps.inputDomain.domainMessageError,
          });
        }
      }
    };

    const onContinue = () => {
      if (withReinforcementModal) {
        setShowReinforcementModal(true);
      }

      if (!withReinforcementModal) {
        handleConfirmChangeDomain();
      }
    };

    const onChangeDomain = (e) => {
      validateDomain(e.target.value.toLowerCase());

      if (e.target.value.length > 3) {
        autoCompleteDomain({ domain: e.target.value.toLowerCase() });
      }

      if (e.target.value.length < 4) {
        setShowAutoCompleteDomain(false);
      }
    };

    const onGetAutocomplete = (domain) => {
      validateDomain(domain);
      setShowAutoCompleteDomain(false);
    };

    const onBack = () => {
      history.goBack();
    };

    return {
      ...defaultProps,
      onChangeDomain,
      onBack,
      onGetAutocomplete,
      onContinue,
      isTemp,
    };
  }, [
    autoCompleteOptions,
    domainIsOk,
    domainsReducer.temporaryDomains,
    newDomain,
    showAutoCompleteDomain,
    autoCompleteDomain,
    changeDomainLocale,
    enabledChangeDomainFlow,
    history,
    params,
    loadingChange,
    withReinforcementModal,
    handleConfirmChangeDomain,
  ]);

  const DomainNotRegisteredProps = useCallback(() => {
    const backButton = () => {
      setCurrentStep(STEPS.INPUT_DOMAIN);
      dispatch(domainsActions.changeDomain.resetState());
    };

    const goToPurchase = () => {
      const domain = newDomain.value.replace(/^www\./, '');
      const domainSld = domain.split('.')[0];
      const domainTld = `.${domain.split('.').slice('1').join('.')}`;

      window.open(`${CART_URL}?pid=d&sld=${domainSld}&tld=${domainTld}&domainCycle=1`, '_blank');

      setCurrentStep(STEPS.NOT_REGISTERED_SUCCESS);
    };

    return {
      locale: changeDomainLocale.steps.domainNotRegistered,
      newDomain,
      mySiteLabel: changeDomainLocale.steps.inputDomain.mySiteLabel,
      backButton,
      goToPurchase,
    };
  }, [changeDomainLocale, newDomain, dispatch]);

  const externalDomainProps = useCallback(() => {
    const originalNameServers = sitesReducer.domainDns;
    const hostNameServers = sitesReducer.planDns;
    const newDomainSetted = domainsReducer.changeDomain.newDomain;

    const returnBack = () => {
      setCurrentStep(STEPS.INPUT_DOMAIN);
      dispatch(sitesActions.domainDns.resetStatus());
      dispatch(domainsActions.changeDomain.resetState());
      dispatch(sitesActions.planDns.reset());
    };

    const continueToSuccess = () => {
      if (newDomainSetted === newDomain.value) {
        setCurrentStep(STEPS.EXTERNAL_SUCCESS);
      }
    };

    const continueLoading = domainsReducer.changeDomain.loading;

    return {
      externalDomainLocale: changeDomainLocale.steps.externalDomain,
      originalNameServers,
      hostNameServers,
      newDomain,
      returnBack,
      continueToSuccess,
      continueLoading,
    };
  }, [
    dispatch,
    changeDomainLocale,
    sitesReducer.domainDns,
    sitesReducer.planDns,
    domainsReducer.changeDomain,
    newDomain,
  ]);

  const externalSuccessProps = useCallback(() => {
    const lastPageName = changeDomainLocale.lastPages[params.lastPage];

    const goToLastPage = () => {
      history.push(`/${params.lastPage}`);
    };

    const goBack = () => {
      setCurrentStep(STEPS.EXTERNAL_DOMAIN);
    };

    return {
      titleLabel: changeDomainLocale.steps.externalSuccess.title,
      descriptionLabel: changeDomainLocale.steps.externalSuccess.description,
      helpLabel: changeDomainLocale.steps.externalSuccess.helpLabel,
      goToLastPageButtonLabel: changeDomainLocale.steps.externalSuccess.backToLastPageButtonLabel(lastPageName),
      goToLastPage,
      goBackLabel: changeDomainLocale.steps.externalSuccess.goBackLabel,
      goBack,
    };
  }, [changeDomainLocale, history, params.lastPage]);

  const getReinforcementModalProps = useCallback(() => {
    const onContinue = () => {
      handleConfirmChangeDomain();
    };

    const onClose = () => {
      setShowReinforcementModal(false);
    };

    return ({
      title: changeDomainLocale.reinforcementModal.title,
      description: changeDomainLocale.reinforcementModal.description,
      originalDomainName: params.originalDomainName,
      newDomain: newDomain.value,
      onClose,
      onContinue,
      continueLabel: changeDomainLocale.reinforcementModal.continue,
      cancelLabel: changeDomainLocale.reinforcementModal.cancel,
    });
  }, [
    changeDomainLocale,
    newDomain.value,
    params.originalDomainName,
    handleConfirmChangeDomain,
  ]);

  const renderStep = {
    [STEPS.INPUT_DOMAIN]: <InputDomain {...getInputDomainProps()} />,
    [STEPS.EXTERNAL_DOMAIN]: (<ExternalDomain {...externalDomainProps()} />),
    [STEPS.SUCCESS]: (<ChangeDomainSuccess {...changeDomainSuccessProps()} />),
    [STEPS.EXTERNAL_SUCCESS]: (<ChangeDomainSuccess {...externalSuccessProps()} />),
    [STEPS.DOMAIN_NOT_REGISTERED]: (<DomainNotRegistered {...DomainNotRegisteredProps()} />),
    [STEPS.NOT_REGISTERED_SUCCESS]: (<ChangeDomainSuccess {...domainNotRegisteredSuccessProps()} />),
  };

  const redirectUserOut = !enabledChangeDomainFlow
    || (hasSites && !hostingData)
    || (hasSites && !canChangeDomain);

  if (!enabledChangeDomainFlow || redirectUserOut) {
    return (
      <Redirect to={routesLocale.sitesPage} />
    );
  }

  if (!domainsReducer.loaded || !domainsReducer.temporaryDomainsLoaded || !sitesReducer.sitesLoaded) {
    return (
      <Styles.ChangeDomainWrapper $centered>
        <Styles.SkeletonWrapper $centered>
          <Styles.TopContent>
            <Skeleton variant="rounded" width="306px" height="24px" />
            <Skeleton variant="rounded" width="368px" height="32px" />
            <Skeleton variant="rounded" width="319px" height="24px" />
          </Styles.TopContent>
          {!mobile && (
            <NavigatorDetail />
          )}
          <Skeleton width="calc(100% - 40px)" height="56px" />
          <Styles.BottomContent>
            <Skeleton width="100%" height="108px" />
            <Skeleton variant="rounded" width={mobile ? '100%' : '126px'} height="48px" />
          </Styles.BottomContent>
        </Styles.SkeletonWrapper>
      </Styles.ChangeDomainWrapper>
    );
  }

  return (
    <Styles.ChangeDomainWrapper>
      {showReinforcementModal && (
        <ReinforcementModal
          {...getReinforcementModalProps()}
        />
      )}

      <Styles.LogoRow>
        <HgLogoV2 newMenu />
      </Styles.LogoRow>

      <Styles.Container>
        <BreadCrumbs breadCrumbs={breadCrumbs} />
        {renderStep[currentStep]}
      </Styles.Container>
    </Styles.ChangeDomainWrapper>
  );
};

export default ChangeDomainHandler;
